'use client';

import { useState } from 'react';

import { MOTION_CONTROL_LABELS } from '@/content/a11y-ui';
import { cn } from '@/lib/utils';

interface MarqueeProps {
  children: React.ReactNode;
  /** Speed in seconds for one full cycle */
  speed?: number;
  /** Reverse scroll direction */
  reverse?: boolean;
  /** Pause on hover */
  pauseOnHover?: boolean;
  /**
   * Fade the left/right edges so labels dissolve at the viewport boundary
   * instead of being hard-clipped mid-word. On by default. Set to `false`
   * for marquees where a crisp edge is wanted (e.g. logo badges).
   */
  fadeEdges?: boolean;
  /**
   * Render the visible pause/play toggle (WCAG 2.2.2 Pause, Stop, Hide: the
   * loop runs longer than five seconds). On by default.
   */
  pauseControl?: boolean;
  className?: string;
}

/**
 * CSS-only infinite horizontal scrolling marquee.
 * Duplicates children for seamless loop.
 * Pauses automatically when prefers-reduced-motion is set, and has a
 * keyboard-reachable pause/play toggle (WCAG 2.2.2).
 */
export function Marquee({
  children,
  speed = 30,
  reverse = false,
  pauseOnHover = false,
  fadeEdges = true,
  pauseControl = true,
  className,
}: MarqueeProps) {
  const [paused, setPaused] = useState(false);

  return (
    <div className="relative">
      <div
        className={cn(
          'w-full max-w-[100vw] overflow-hidden [contain:paint_layout]',
          // Soft fade at both horizontal edges so category labels (and stray
          // loop fragments) dissolve at the boundary rather than clipping
          // mid-word at the left/right viewport edge.
          fadeEdges &&
            '[mask-image:linear-gradient(to_right,transparent,black_clamp(16px,4vw,80px),black_calc(100%-clamp(16px,4vw,80px)),transparent)] [-webkit-mask-image:linear-gradient(to_right,transparent,black_clamp(16px,4vw,80px),black_calc(100%-clamp(16px,4vw,80px)),transparent)]',
          className,
        )}
        aria-hidden="true"
      >
        <div
          className={cn(
            'flex w-max',
            reverse ? 'animate-marquee-reverse' : 'animate-marquee',
            pauseOnHover && 'hover:[animation-play-state:paused]',
            paused && '[animation-play-state:paused]',
          )}
          style={{ animationDuration: `${speed}s` }}
        >
          <div className="flex shrink-0">{children}</div>
          <div className="flex shrink-0">{children}</div>
        </div>
      </div>
      {pauseControl && (
        <button
          type="button"
          onClick={() => setPaused((p) => !p)}
          aria-pressed={paused}
          aria-label={
            paused
              ? MOTION_CONTROL_LABELS.playMarquee
              : MOTION_CONTROL_LABELS.pauseMarquee
          }
          className="absolute bottom-0 right-2 z-10 flex size-7 translate-y-1/2 items-center justify-center rounded-full border border-border/40 bg-background/85 text-foreground backdrop-blur-sm transition-colors hover:border-accent hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent motion-reduce:hidden md:right-4"
        >
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className="size-3"
            aria-hidden="true"
          >
            {paused ? (
              <path d="M8 5v14l11-7z" />
            ) : (
              <path d="M7 5h4v14H7zM13 5h4v14h-4z" />
            )}
          </svg>
        </button>
      )}
    </div>
  );
}
